import React from 'react'

import { useHistory, useParams, useRouteMatch } from 'react-router'
import { useTranslation } from 'react-i18next'
import gql from 'graphql-tag'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faPlus, faEdit, faHome } from '@fortawesome/pro-light-svg-icons'
import ProjectModal from 'components/Modal/ProjectModal'
import ScenarioEditionModal from 'components/Modal/ScenarioEditionModal'

import { scenarioScore } from 'utils/ranking'
import { Tag, Tooltip } from 'antd'
import {
	useCreateScenarioMutation,
	useDeleteScenarioMutation,
	useUpdateConstraintScenariosMutation,
	useUpdateIndicatorScenariosMutation,
	useUpdateScenarioMutation,
	useUpdateStakefactorScenariosMutation,
} from 'graphql/generated'

/*
<a
				className={[useRouteMatch('/projects/' + params?.id + '/fishbone') ? 'selected' : ''].join(' ')}
				onClick={() => {
					history.push('/projects/' + params?.id + '/fishbone')
				}}
			>
				{t('pages.fishbone.title')}
			</a>
*/

import './ProjectNavigation.sass'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { findIndex, pick } from 'lodash'
import { handleCreateScenario } from 'utils/graphql'

function ProjectNavigation(props: any) {
	let history = useHistory()
	const params: any = useParams()
	const { t } = useTranslation()

	const [editedItem, setEditedItem] = React.useState<any | null>(null)
	const [editedScenario, setEditedScenario] = React.useState<any | null>(null)

	let score = scenarioScore(props, props.scenario)

	const [createScenario] = useCreateScenarioMutation()
	const [deleteScenario] = useDeleteScenarioMutation()
	const [updateScenario] = useUpdateScenarioMutation()

	const [updateIndicatorScenarios] = useUpdateIndicatorScenariosMutation()
	const [updateConstraintScenarios] = useUpdateConstraintScenariosMutation()
	const [updateStakefactorScenarios] = useUpdateStakefactorScenariosMutation()

	const handleAskToCreateScenario = async (referenceScenario: any) => {
		let scenarioCount = props.scenarios.length

		props.pushModal(
			<ScenarioEditionModal
				onHide={() => {
					props.popModal()
				}}
				onSave={async (values: any) =>
					handleCreateScenario(
						props.project.id,
						props,
						referenceScenario,
						values,
						() => {
							props.popModal()
							props.onGoToScenario(scenarioCount)
						},
						{
							createScenario,
							updateIndicatorScenarios,
							updateConstraintScenarios,
							updateStakefactorScenarios,
						}
					)
				}
				id={false}
				values={{
					name:
						referenceScenario && referenceScenario.name !== null ? referenceScenario.name + ' - Copy' : '',
					description:
						referenceScenario && referenceScenario.description !== null
							? referenceScenario.description
							: '',
				}}
			></ScenarioEditionModal>
		)
	}

	// Update
	const handleUpdateScenario = async (id: string, values: Object) => {
		let index = findIndex(props.scenarios, (s: any) => s.id === id)
		console.log('index', index)
		let newScenario = {
			...pick(props.scenarios[index], ['description', 'name']),
			...pick(values, ['description', 'name']),
		}

		console.log('handleUpdateScenario', newScenario)

		const result = await updateScenario({
			variables: {
				id: id,
				...newScenario,
			},
			update(cache, { data: { updateScenario } }) {
				cache.modify({
					id: `Scenario:${id}`,
					fields: {
						name() {
							return updateScenario.name || newScenario.name
						},
						description() {
							return updateScenario.description || newScenario.description
						},
					},
				})
			},
			optimisticResponse: {
				updateScenario: {
					__typename: 'Scenario',
					id,
					...newScenario,
				},
			},
		})
	}

	const askDeleteConfirmation = async (id?: string) => {
		props.pushModal(
			<ConfirmModal
				title={t('models.scenario.messages.delete_confirmation')}
				t={t}
				onConfirm={() => handleDelete(id)}
				message={t('models.scenario.messages.delete_confirmation_explanation')}
			></ConfirmModal>
		)
	}

	// Delete
	const handleDelete = async (id: string | undefined) => {
		console.log('deleteScenario', id)

		if (id == null) {
			return
		}
		props.onGoToPreviousScenario()
		await deleteScenario({
			variables: { id: id },
			update(cache, { data: { deleteScenario } }) {
				cache.modify({
					id: `Project:${props.project.id}`,
					fields: {
						scenarios(existingScenariosRef, { readField }) {
							return existingScenariosRef.filter((ref: string) => id !== readField('id', ref))
						},
					},
				})
			},

			optimisticResponse: {
				deleteScenario: {
					__typename: 'Scenario',
					id: id,
				},
			},
		})
	}

	return (
		<div className="ProjectNavigation noselection">
			<div className="title text-nowrap d-flex align-items-center">{props.project?.name} </div>

			<div className="separator"></div>

			<div className="scenario">
				<div className="navigation mr-1">
					<a onClick={props.onGoToPreviousScenario} className="chevron">
						<FontAwesomeIcon icon={faChevronLeft} />
					</a>
					{props.scenarioIndex !== null ? props.scenarioIndex + 2 : 1} /
					{props.scenarioCount !== undefined ? props.scenarioCount + 1 : 1}
					<a onClick={props.onGoToNextScenario} className="chevron">
						<FontAwesomeIcon icon={faChevronRight} />
					</a>
				</div>
				{props.scenario?.description ? (
					<Tooltip title={props.scenario?.description}>{props.scenario?.name || 'Baseline'}</Tooltip>
				) : (
					props.scenario?.name || 'Baseline'
				)}

				<div className="ml-2">
					{props.scenarioIndex !== null && (
						<Tooltip title={t('models.scenario.actions.clone')}>
							<Tag
								onClick={() => {
									handleAskToCreateScenario(props.scenario)
								}}
								className={'tag unselected'}
							>
								<FontAwesomeIcon icon={faPlus} />
							</Tag>{' '}
						</Tooltip>
					)}

					{props.scenarioIndex !== null && (
						<Tooltip title={t('models.scenario.actions.edit')}>
							<Tag
								onClick={() => {
									setEditedScenario(props.scenario)
								}}
								className={'tag unselected'}
							>
								<FontAwesomeIcon icon={faEdit}></FontAwesomeIcon>
							</Tag>{' '}
						</Tooltip>
					)}

					{props.scenarioIndex === null && (
						<Tooltip title={t('models.scenario.actions.create_new')}>
							<Tag onClick={() => handleAskToCreateScenario()} className={'tag unselected'}>
								<FontAwesomeIcon icon={faPlus} />
							</Tag>{' '}
						</Tooltip>
					)}
				</div>
			</div>

			{editedItem && (
				<ProjectModal
					onHide={() => {
						setEditedItem(null)
					}}
					onSave={(values: Object) => {
						props.onUpdate(editedItem.id, values)
						setEditedItem(null)
					}}
					id={editedItem?.id}
					values={{
						name: editedItem?.name,
						description: editedItem?.description,
					}}
				></ProjectModal>
			)}

			{editedScenario && (
				<ScenarioEditionModal
					onHide={() => {
						setEditedScenario(null)
					}}
					onDelete={() => {
						askDeleteConfirmation(editedScenario.id)
						setEditedScenario(null)
					}}
					onSave={(values: Object) => {
						handleUpdateScenario(editedScenario.id, values)
						setEditedScenario(null)
					}}
					id={editedScenario?.id}
					values={{
						name: editedScenario?.name,
						description: editedScenario?.description || '',
					}}
				></ScenarioEditionModal>
			)}
		</div>
	)
}

export default ProjectNavigation
